export const meta = {
  id: '08-lunar-and-solar-classes',
  order: 8,
  checkFocus: { cases: ['acc'] },   // what the end-of-lesson check tests first
  titleFi: 'Kuusuku ja aurinkosuku',
  titleEn: 'Lunar and solar classes',
  duolingoUnit: ['duo-06-lunar-class', 'duo-07-solar-class'],
  // drējī: November's Halloween line (hl-2026-11, together/halloween); with the rising pace (2026-10-14) the quotas
  // would bring it only in lesson 9
  lemmaIds: ['jentys', 'ñuha', 'tubis', 'drējī', 'ours:ystys', 'ours:ulōrtys', 'ours:dohaertys', 'ours:aneverys', 'ours:ulōrvio', 'ours:zūgio', 'ours:duptys', 'ours:ibardukio', 'ours:suenos', 'ours:mazverdiros', 'ours:hulio', 'ours:vegetariani', 'ours:iettys', 'ours:veterinari', 'ours:dentisti', 'ours:juktys', 'ours:advokati', 'ours:juristi', 'ours:reporteri', 'ours:teptāzma', 'ours:komisari', 'ours:koloneli', 'ours:majori', 'ours:lūtnanti', 'ours:sergenti', 'ours:sherifi', 'ours:homoseksuali', 'ours:sōverys', 'ours:sykiatri'],
  exampleIds: [
    'got-s7e04-001',
    'ex-d074af51ccf2',
    'ex-1fc7f7ad68ce',
    'ex-1b0b3dd072a2',
    'got-s3e04-008',
    'ex-0cb563e580b2',
    'hotd-s1e02-005',
    'got-s3e05-003',
    'ex-c63fa45474e2',
    'ex-9d1b7ab86863',
    'got-s4e06-003',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Lunar_class',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Noun_Declensions',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Gender',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Gender',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Possessive_Adjectives',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Solar_class',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Pronouns#Demonstrative_and_interrogative_pronouns',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['declensions', 'luokat'],
  grammarAlso: ['genders', 'adj-agreement'],
  showLines: ['got-s7e04-001', 'got-s3e04-008', 'hotd-s1e02-005', 'got-s3e05-003', 'got-s4e06-003'],
  pronunciation: { focus: 'r', words: ['embar', 'hūra', 'qogror', 'hūrenka', 'ābra'], lines: ['got-s7e04-001', 'got-s3e04-008', 'got-s3e05-003'] },
  letter: {
    fi: 'Kirjoita kolme lausetta perheestäsi tai ystävistäsi kuu- ja aurinkosukuisilla sanoilla.',
    en: 'Write three sentences about your family or friends with lunar and solar nouns.',
  },
  scene: 'got-s4e06-1',
}

# Kuusuku ja aurinkosuku

Tällä viikolla kaksi neljästä suvusta. Kuusukuun kuuluu moni ihmistä tarkoittava sana, ja aurinkosuku on korkeavalyrian yleisin <Term id="luokka">substantiiviluokka</Term>. Kummastakin opetellaan monikko, akkusatiivi ja se, miten adjektiivi mukautuu sanaan.

## Kuusuku

Ensimmäinen neljästä suvusta on kuusuku. Duolingon Tips & Notes -ohjeiden mukaan se on korkeavalyrian <Term id="luokka">substantiiviluokista</Term> monipuolisin, ja moni tärkeä ihmistä tarkoittava sana kuuluu siihen. Siksi kuusuvun päätteet kannattaa opetella huolella jo nyt.

## Ihmisten suku

Moni ihmiseen viittaava sana on kuusukuinen. Olette jo tavanneet niistä useita: <Hv glossed>vala</Hv> ’mies’, <Hv glossed>ābra</Hv> ’nainen’ ja <Hv glossed>kepa</Hv> ’isä’. Samaan sukuun kuuluvat esimerkiksi <Hv glossed>muña</Hv> ’äiti’, <Hv glossed>riña</Hv> ’tyttö, lapsi’, <Hv glossed>tala</Hv> ’tytär’, <Hv glossed>trēsy</Hv> ’poika (jonkun poika)’ ja <Hv glossed>āeksio</Hv> ’herra, isäntä’.

Kaikki ihmiset eivät silti ole kuusukuisia. Wikin kieliopin mukaan ihmisiä tarkoittavat sanat ovat useimmiten joko kuu- tai aurinkosukuisia; esimerkiksi <Hv glossed>azantys</Hv> ’ritari’ on aurinkosukuinen.

<Note>
**Sanan tarina.** Peterson on nimennyt sanoja ihmisten kunniaksi. <Hv glossed>Tala</Hv> ’tytär’ tulee hänen pikkusiskonsa Natalien nimestä: koko nimi oli liian pitkä vartaloksi, joten hän otti siitä osan *tal*. <Hv glossed>Trēsy</Hv> ’poika’ on tehty hänen 3 000. Twitter-seuraajansa kunniaksi. Tarinat ovat wikin sanasivuilla; niitä on koonnut jan Misali (Conlang Critic).
</Note>

<Line id="got-s7e04-001" />

Daenerysin lohduttavassa repliikissä <Hv glossed>muña</Hv> ’äiti’ on kuusukuinen sana nominatiivissa: <Hv glossed>Muña mastas</Hv> ’Äiti on tullut’.

## Viisi teemavokaalia

Duolingon ohjeiden mukaan kuusukuisen substantiivin teemavokaali voi olla *a*, *e*, *i*, *o* tai *y*. Teemavokaali on se vokaali, joka näkyy nominatiivissa sanan lopussa, ja se kertoo, miten sana taipuu. Wikin taivutussivulla kutakin vokaalia vastaa oma <Term id="deklinaatio">taivutusluokkansa</Term>:

- **a**: <Hv>vala</Hv>, <Hv>ābra</Hv>, <Hv>muña</Hv>
- **y**: <Hv>trēsy</Hv>, <Hv glossed>azandy</Hv> ’lyhytmiekka’
- **o**: <Hv glossed>pēko</Hv> ’oliivi’, <Hv l="āeksio">āeksio</Hv>
- **e**: <Hv glossed>korze</Hv> ’pitkämiekka’, <Hv glossed>gelte</Hv> ’kypärä’
- **i**: <Hv glossed>brōzi</Hv> ’nimi’, <Hv glossed>kēli</Hv> ’kissa’

<Note>
Damien Halme (Academy of High Valyrian) on antanut kuudelle <Term id="deklinaatio">taivutusluokalle</Term> lempinimet sen mukaan, mitkä suvut niihin kuuluvat: Vuorovesi, Taivas, Mestari, Kuiva, Märkä ja Vieras. Kuusuku on mukana neljässä niistä. Nimet ja niiden selitykset ovat Kieliopissa: <TopicLink id="declensions" />.
</Note>

<Pronounce word="korze" />

<Pronounce word="azandy" />

## Monikko ja akkusatiivi: -i

Ohjeiden pääsääntö on, että useimmat kuusukuiset sanat saavat **monikossa ja akkusatiivissa jonkinlaisen *-i*-päätteen**.

- Monikko: <Hv>vala</Hv> → <Seam s="val" e="i" />, <Hv>korze</Hv> → <Seam l="korze" s="korz" e="i" />, <Hv>azandy</Hv> → <Seam s="azand" e="i" />.
- Yksikön akkusatiivi: <Hv>azandy</Hv> → <Seam s="azand" e="i" />, <Hv>trēsy</Hv> → <Seam s="trēs" e="i" />.
- Monikon akkusatiivissa vokaali on pitkä: <Seam s="val" e="ī" />, <Seam l="korze" s="korz" e="ī" />, <Seam s="azand" e="ī" />.

### Kun -a ja -i sulautuvat: -e

Kun sana päättyy *a*:han, yksikön akkusatiivin *-i* sulautuu tähän *-a*:han, ja tuloksena on *-e*. Siksi <Hv>vala</Hv> on akkusatiivissa <Seam s="val" e="e" /> ja <Hv>ābra</Hv> on <Seam s="ābr" e="e" />. Tämä on sama *-a → -e*, jonka opitte jo Perusteet 2 -oppitunnilla. Nyt tiedätte, mistä se tulee.

<Paradigm lemma="vala" cases="nom,acc" numbers="sg,pl" />

<Example id="ex-d074af51ccf2" />

<Hv glossed>Tale</Hv> on sanan <Hv glossed>tala</Hv> ’tytär’ akkusatiivi: isä uskoi tytärtään. Lause on menneessä ajassa, mutta akkusatiivi on tuttu.

### -e-loppuiset: yksi muoto, kaksi merkitystä

Ohjeet nostavat esiin yhden erikoisuuden. *-e*-loppuisilla sanoilla yksikön akkusatiivikin on *-ī*, eli täsmälleen sama kuin monikon akkusatiivi. <Seam glossed s="korz" e="ī" /> voi siis objektina tarkoittaa sekä ’pitkämiekan’ että ’pitkämiekat’.

<Paradigm lemma="korze" cases="nom,acc" numbers="sg,pl" />

Silloin lukumäärän kertoo asiayhteys.

<Paradigm lemma="azandy" cases="nom,acc" numbers="sg,pl" />

Sanassa <Hv>azandy</Hv> taas yksikön akkusatiivi ja monikon nominatiivi ovat samat: <Seam s="azand" e="i" />.

### Tässä suomi auttaa vähän

Suomalaiselle *i* monikon tunnuksena ei ole vieras. Suomen monikon taivutusmuodoissa on *i*: *talo-i-ssa*, *nais-i-a*, *mieh-i-llä*. Korkeavalyriassa *-i* on usein suoraan sanan lopussa. Se kuulostaa erilaiselta, mutta ajatus on tuttu.

## Kun sääntö ei riitä: -o ja -i

Ohjeiden mukaan kuusukuisten sanojen monikon akkusatiivi on aina *-ī*. Tämä pitää paikkansa *a*-, *e*- ja *y*-loppuisista sanoista. Wikin taivutustaulukoissa *o*- ja *i*-loppuiset kuusukuiset sanat taipuvat kuitenkin toisin: niiden monikko päättyy *-a*:han, ja nominatiivi ja akkusatiivi ovat samat sekä yksikössä että monikossa.

<Paradigm lemma="pēko" cases="nom,acc" numbers="sg,pl" />

<Paradigm lemma="brōzi" cases="nom,acc" numbers="sg,pl" />

<Hv>Pēko</Hv> → <Seam s="pēk" e="a" /> ja <Hv l="brōzi">brōzi</Hv> → <Seam s="brōz" e="a" />. Kun näette kuusukuisen sanan, joka päättyy *-o*:hon tai *-i*:hin, älkää odottako *-ī*:tä. Näihin tyyppeihin palataan tarkemmin myöhemmin.

<Example id="ex-1fc7f7ad68ce" />

Tässä <Hv glossed>kēli</Hv> ’kissa’ ja <Hv glossed>āeksio</Hv> ’isäntä’ ovat kumpikin kuusukuisia, ja <Hv glossed>ñuha</Hv> ’minun’ on kuusuvun muodossa.

## Adjektiivi kuusukuisen sanan kanssa

Kun adjektiivi kuvaa kuusukuista substantiivia, se saa kuusuvun päätteet. Monella adjektiivilla ne näyttävät samalta kuin kuusukuisten *a*-sanojen päätteet: *-a*, monikossa *-i*, akkusatiivissa *-e* ja monikon akkusatiivissa *-ī*.

<Paradigm lemma="ñuha" filter="positive.pre.lunar" cases="nom,acc" numbers="sg,pl" />

<Example id="ex-1b0b3dd072a2" />

<Interlinear hv="Ñuh|a muñ|a bōs|i azant|i jorrāel|za." gloss="minun-LUN.NOM äiti-NOM pitkä-SOL.ACC ritari-ACC rakastaa-PRS.3SG" fi="Äitini rakastaa pitkää ritaria." />

Vertaa lauseen kahta adjektiivia. <Hv l="ñuha">Ñuha</Hv> kuvaa kuusukuista äitiä ja on kuusuvun nominatiivissa. <Hv>Bōsi</Hv> kuvaa aurinkosukuista ritaria ja on aurinkosuvun akkusatiivissa. Suomen *pitkää* mukautuu vain sijaan ja lukuun, korkeavalyrian adjektiivi myös sukuun.

## Harjoitellaan

<Practice hv="Vala korzī urnes." fi="Mies näkee pitkämiekan." />

<Practice hv="Muña riñe urnes." fi="Äiti näkee tytön." />

<Practice hv="Ñuha kepa trēsi urnes." fi="Isäni näkee pojan." />

<Practice hv="Ābri pēka urnesi." fi="Naiset näkevät oliivit." />

<Practice hv="Riña ñuhe muñe urnes." fi="Tyttö näkee äitini." />

Viimeisessä lauseessa sekä adjektiivi että substantiivi ovat kuusuvun akkusatiivissa: <Seam s="ñuh" e="e" /> <Seam s="muñ" e="e" />.

## Aurinkosuku

Duolingon Tips & Notes -ohjeiden mukaan aurinkosuku on todennäköisesti korkeavalyrian yleisin <Term id="luokka">substantiiviluokka</Term>. Sen sanat on helppo tunnistaa, mutta sen adjektiiveissa on jotain uutta: pääte, joka riippuu adjektiivin paikasta. Siihen suomi ei valmista lainkaan.

## Tunnusmerkki: s tai z

Duolingon ohjeiden mukaan **kaikkien aurinkosukuisten substantiivien nominatiivin päätteessä on *s* tai *z***. Olette jo tavanneet monta: <Hv glossed>vēzos</Hv> ’aurinko’, <Hv glossed>azantys</Hv> ’ritari’, <Hv glossed>hontes</Hv> ’lintu’. Tämän osion uudet sanat ovat <Hv glossed>jentys</Hv> ’johtaja’ ja <Hv glossed>tubis</Hv> ’päivä’.

<Pronounce word="jentys" />

<Pronounce word="tubis" />

Wikin sukusivun mukaan aurinkosukuun kuuluu ihmisiä ja erityisesti ammatteja (<Hv>azantys</Hv>, <Hv glossed>dārys</Hv> ’kuningas’), päiväeläimiä (<Hv glossed>zaldrīzes</Hv> ’lohikäärme’) ja ruumiinosia.

<Line id="got-s3e04-008" />

Daenerys esittäytyy: <Hv glossed>ēngos</Hv> ’kieli’ on aurinkosukuinen, ja niin on ’minun’-sanan muoto <Hv>ñuhys</Hv>.

<Example id="ex-0cb563e580b2" />

## Monikko: kaksois-s ja teemavokaali

Ohjeiden mukaan aurinkosukuisten sanojen monikon nominatiivissa on tyypillisesti **kaksois-*ss* ja sen perässä teemavokaali**:

- <Hv>jentys</Hv> → <Seam s="jent" e="yssy" />
- <Hv>tubis</Hv> → <Seam s="tub" e="issa" />
- <Hv>hontes</Hv> → <Seam s="hont" e="esse" />
- <Hv l="vēzos">vēzos</Hv> → <Seam s="vēz" e="ossa" />
- <Hv>zaldrīzes</Hv> → <Seam s="zaldrīz" e="esse" />

<Pronounce word="jentyssy" />

<Pronounce word="tubissa" />

Kaksoiskonsonantti on suomalaiselle helppo: se äännetään pitkänä, kuten suomen sanassa *kissa*. Ja koska tavu päättyy konsonanttiin, se on raskas ja saa painon: *jen-TYS-sy*, *tu-BIS-sa*.

<Line id="hotd-s1e02-005" />

Lohikäärmeenhoitajan repliikin viimeisessä lauseessa on <Hv glossed>tubissa</Hv> ’päivät’. Sanatarkasti lause kuuluu ’päivät kestävät, ennen kuin poika toipuu’.

## Akkusatiivi: kuin kuusuvussa

Ohjeiden mukaan aurinko- ja kuusukuisten sanojen akkusatiivipäätteet ovat samankaltaisia. *-ys*-loppuiset sanat saavat yksikössä *-i*:n ja monikossa *-ī*:n, aivan kuten opitte Perusteet 2 -oppitunnilla.

<Paradigm lemma="jentys" cases="nom,acc" numbers="sg,pl" />

<Line id="got-s3e05-003" />

Daenerys käskee Tahrattomia valitsemaan johtajansa: <Hv glossed>jenti</Hv> on yksikön akkusatiivi ’johtajan’.

Kaikki aurinkosukuiset sanat eivät kuitenkaan muutu akkusatiivissa. Wikin taivutustaulukoissa *-is*- ja *-os*-loppuiset sanat, kuten <Hv>tubis</Hv> ja <Hv l="vēzos">vēzos</Hv>, näyttävät akkusatiivissa samalta kuin nominatiivissa:

<Paradigm lemma="tubis" cases="nom,acc" numbers="sg,pl" />

<Example id="ex-c63fa45474e2" />

’Tämä päivä’ on tässä <Hv glossed>kesys tubis</Hv>. Osoittava adjektiivi <Hv l="kesa">kesa</Hv> (tuttu osoittavien pronominien oppitunnilta) on aurinkosuvun muodossa, koska <Hv>tubis</Hv> on aurinkosukuinen.

## Adjektiivi aurinkosukuisen sanan kanssa

Ohjeiden mukaan aurinkosukuista sanaa kuvaava adjektiivi saa omat päätteensä, kuten voi odottaa. Uutta on se, että **osa päätteistä riippuu siitä, onko adjektiivi substantiivin edellä vai jäljessä**.

Ohjeiden esimerkki on ’minun johtajani’ monikossa. Otetaan sanan ’johtaja’ monikko <Hv glossed>jentyssy</Hv> ja sen eteen ’minun’-sanan aurinkosukuinen monikko:

<Compare glossed fi="minun johtaja|ni" hv="ñuh|yz jent|yssy" label="adjektiivi edellä (etuasema)" />

Kun järjestys käännetään, adjektiivin loppuun palaa vokaali, joka tavallisesti putoaa pois:

<Compare glossed fi="johtaja|ni" hv="jent|yssy ñuh|yzy" label="adjektiivi jäljessä (jälkiasema)" />

<Pronounce word="ñuhyzy" />

<Paradigm lemma="ñuha" filter="positive.pre.solar" cases="nom,acc" numbers="sg,pl" />

<Paradigm lemma="ñuha" filter="positive.post.solar" cases="nom,acc" numbers="sg,pl" />

Vertaa taulukoita: ero on monikon nominatiivissa. Muut muodot ovat samat.

### Tässä suomesta ei ole lainkaan apua

Suomen adjektiivi on samanlainen paikasta riippumatta. *Hyvät miehet* ja runollinen *miehet hyvät* käyttävät samaa muotoa. Korkeavalyriassa paikka voi muuttaa päätettä. Tämä on opeteltava uutena asiana, ja aurinkosuvun monikko on ensimmäinen kohta, jossa se tulee vastaan.

## z muuttuu s:ksi

Ohjeissa on vielä yksi tarkennus. Kun aurinkosuvun monikkomuotoinen adjektiivi on sanan edellä ja seuraava sana alkaa soinnittomalla konsonantilla (esimerkiksi *k*, *p*, *s*, *t*, *h* tai *q*), adjektiivin *z* muuttuu *s*:ksi (Peterson: <Hv fi="siniset vuohet">kastys hobresse</Hv>):

- <Hv glossed>ñuhyz jentyssy</Hv> ’minun johtajani’ – *z*, koska seuraava sana alkaa *j*:llä
- <Hv glossed>ñuhys tubissa</Hv> ’minun päiväni’ – *s*, koska seuraava sana alkaa *t*:llä
- <Hv glossed>tubissa ñuhyzy</Hv> ’minun päiväni’ – *z* palaa, kun järjestys käännetään

Muutos on luonteva: soinnillinen *z* muuttuu soinnittomaksi *s*:ksi soinnittoman konsonantin edellä. Suomalaisen kannattaa tässä varoa erityisesti päinvastaista virhettä. Kun *z* pysyy *z*:nä, se on äännettävä soinnillisena eikä suomalaisittain *s*:nä.

<Note>
<Hv glossed>Ñuhys</Hv> on myös ’minun’-sanan aurinkosukuinen yksikkö: <Hv glossed>ñuhys raqiros</Hv> on ’ystäväni’. Siksi <Hv>ñuhys</Hv> voi olla yksikkö tai – soinnittomalla konsonantilla alkavan sanan edellä – monikko. Substantiivi kertoo, kummasta on kyse.
</Note>

<Example id="ex-9d1b7ab86863" />

<Interlinear hv="Skoriot ñuh|yz zaldrīz|esse issi?!" gloss="missä minun-SOL.NOM.PL lohikäärme-NOM.PL olla-PRS.3PL" fi="Missä lohikäärmeeni ovat?!" />

<Hv>Zaldrīzesse</Hv> alkaa *z*:lla, joka on soinnillinen, joten adjektiivi pysyy muodossa <Hv>ñuhyz</Hv>.

<Line id="got-s4e06-003" />

Tässä <Hv glossed>ñuhys raqiros</Hv> ’ystäväni’ on yksikössä.

## Harjoitellaan

<Practice hv="Ñuhys jentys hontī urnes." fi="Johtajani näkee linnun." />

<Practice hv="Ñuhyz jentyssy azantī urnesi." fi="Johtajani näkevät ritarit." />

<Practice hv="Jentyssy ñuhyzy azantī urnesi." fi="Johtajani näkevät ritarit." />

<Practice hv="Ābra ñuhi jenti urnes." fi="Nainen näkee johtajani." />

Kaksi keskimmäistä lausetta tarkoittavat samaa. Vain adjektiivin paikka ja pääte vaihtuvat. Viimeisessä lauseessa ’minun’ on aurinkosuvun yksikön akkusatiivissa, koska johtaja on objekti.

## Sanat

<Vocab />

<Check />
